import {
  DISTRIBUTOR_CHANNEL_WORDS,
  DISTRIBUTOR_DATA_POINTS,
  SUPPLIER_DATA_POINTS,
  distributorResearchAnswerSchema,
  distributorResearchAskSchema,
  researchAnswerSchema,
  researchAskSchema,
  supplierResearchAnswerSchema,
  supplierResearchAskSchema,
  type DistributorResearchAnswer,
  type DistributorResearchAsk,
  type ResearchAnswer,
  type ResearchAsk,
  type SupplierResearchAnswer,
  type SupplierResearchAsk,
} from '@norum/contracts';
import { Badge, Kicker } from '@/ui/primitives';
import { productName } from '@/prospecting/words';

/**
 * A research ask or answer, read as what it is instead of the JSON that
 * crossed (ADR-0132, ADR-0162). The text is parsed against the same
 * schemas the receiver wrote it under; anything that does not parse is
 * shown as it came, so nothing is hidden by a view that did not fit.
 */

export type ParsedResearch =
  | { kind: 'market_ask'; ask: ResearchAsk }
  | { kind: 'supplier_ask'; ask: SupplierResearchAsk }
  | { kind: 'market_answer'; answer: ResearchAnswer }
  | { kind: 'supplier_answer'; answer: SupplierResearchAnswer }
  | { kind: 'distributor_ask'; ask: DistributorResearchAsk }
  | { kind: 'distributor_answer'; answer: DistributorResearchAnswer };

export function parseResearch(text: string | null | undefined): ParsedResearch | null {
  if (!text) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  const marketAnswer = researchAnswerSchema.safeParse(raw);
  if (marketAnswer.success) return { kind: 'market_answer', answer: marketAnswer.data };
  const supplierAnswer = supplierResearchAnswerSchema.safeParse(raw);
  if (supplierAnswer.success) return { kind: 'supplier_answer', answer: supplierAnswer.data };
  const marketAsk = researchAskSchema.safeParse(raw);
  if (marketAsk.success) return { kind: 'market_ask', ask: marketAsk.data };
  const supplierAsk = supplierResearchAskSchema.safeParse(raw);
  if (supplierAsk.success) return { kind: 'supplier_ask', ask: supplierAsk.data };
  const distributorAnswer = distributorResearchAnswerSchema.safeParse(raw);
  if (distributorAnswer.success) return { kind: 'distributor_answer', answer: distributorAnswer.data };
  const distributorAsk = distributorResearchAskSchema.safeParse(raw);
  if (distributorAsk.success) return { kind: 'distributor_ask', ask: distributorAsk.data };
  return null;
}

/** A page's name as a person would say it: the search's own title, else its host. */
export function pageLabel(url: string, title?: string | null): string {
  if (title && title.trim()) return title.trim();
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

const POINT_LABEL = new Map<string, string>(SUPPLIER_DATA_POINTS.map((p) => [p.key, p.label]));
const DISTRIBUTOR_LABEL = new Map<string, string>(DISTRIBUTOR_DATA_POINTS.map((p) => [p.key, p.label]));

function PageLink({ url, title }: { url: string; title?: string | null }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-dense text-accent underline-offset-2 hover:underline [overflow-wrap:anywhere]">
      {pageLabel(url, title)}
    </a>
  );
}

/** The raw text, folded away, for whoever wants exactly what crossed. */
function AsItCrossed({ text }: { text: string }) {
  let pretty = text;
  try {
    pretty = JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    // Not JSON: shown as it came.
  }
  return (
    <details className="text-dense text-secondary">
      <summary className="cursor-pointer text-label uppercase tracking-label">As it crossed</summary>
      <pre className="m-0 mt-2 max-h-80 overflow-auto whitespace-pre-wrap border border-line bg-raised p-3 text-micro [overflow-wrap:anywhere]">{pretty}</pre>
    </details>
  );
}

export function ResearchAskView({ text }: { text: string }) {
  const parsed = parseResearch(text);
  if (parsed?.kind === 'market_ask') {
    const { ask } = parsed;
    return (
      <div className="flex flex-col gap-2" data-testid="research-ask">
        <p className="m-0 text-body">
          Research {ask.country}
          {ask.product ? ` for ${productName(ask.product)}` : ''}: {ask.criteria.length} criteria, each answered from sources or said to be not found.
        </p>
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {ask.criteria.map((c) => (
            <li key={c}>
              <Badge plain>{c}</Badge>
            </li>
          ))}
        </ul>
        {ask.sources.length > 0 && (
          <p className="m-0 text-dense text-secondary">
            Read first: {ask.sources.map((s, i) => (
              <span key={s.id}>
                {i > 0 ? ', ' : ''}
                <PageLink url={s.url} title={s.title} />
              </span>
            ))}
          </p>
        )}
        <AsItCrossed text={text} />
      </div>
    );
  }
  if (parsed?.kind === 'supplier_ask') {
    const { ask } = parsed;
    return (
      <div className="flex flex-col gap-2" data-testid="research-ask">
        <p className="m-0 text-body">
          Find up to {ask.maxCandidates} manufacturers in {ask.country} of {ask.products.map(productName).join(' and ')}, each read from its own website.
        </p>
        <AsItCrossed text={text} />
      </div>
    );
  }
  if (parsed?.kind === 'distributor_ask') {
    const { ask } = parsed;
    return (
      <div className="flex flex-col gap-2" data-testid="research-ask">
        <p className="m-0 text-body">
          Find up to {ask.maxCandidates} distributors in {ask.country} that supply {ask.channels.map((c) => DISTRIBUTOR_CHANNEL_WORDS[c]).join(', ')}, each read from its own website.
        </p>
        <AsItCrossed text={text} />
      </div>
    );
  }
  return <p className="m-0 whitespace-pre-wrap text-body">{text}</p>;
}

export function ResearchAnswerView({ text }: { text: string }) {
  const parsed = parseResearch(text);
  if (parsed?.kind === 'market_answer') return <MarketAnswer answer={parsed.answer} text={text} />;
  if (parsed?.kind === 'supplier_answer') return <SupplierAnswer answer={parsed.answer} text={text} />;
  if (parsed?.kind === 'distributor_answer') return <DistributorAnswer answer={parsed.answer} text={text} />;
  return <p className="m-0 whitespace-pre-wrap text-body">{text}</p>;
}

function MarketAnswer({ answer, text }: { answer: ResearchAnswer; text: string }) {
  const found = answer.findings.filter((f) => f.provenance === 'sourced');
  const missing = answer.findings.filter((f) => f.provenance === 'not_found');
  return (
    <div className="flex flex-col gap-4" data-testid="research-answer">
      <p className="m-0 text-dense text-secondary">
        {answer.marketCode}
        {answer.product ? ` · ${productName(answer.product)}` : ''} · researched {answer.researchedOn} · {found.length} of {answer.findings.length} found from sources
        {missing.length > 0 ? `, ${missing.length} not found` : ''}
      </p>
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {answer.findings.map((f) => (
          <li key={f.criterion} className="flex flex-col gap-1.5 border-l-2 border-line pl-3" data-testid="research-finding">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-dense font-semibold">{f.criterion}</span>
              {f.provenance === 'sourced' ? (
                (f.checked?.length ?? 0) > 0 ? (
                  <Badge tone="accent">From sources · read back</Badge>
                ) : (
                  <Badge tone="warning">From sources · not read back</Badge>
                )
              ) : (
                <Badge tone="neutral">Looked for, not found</Badge>
              )}
              {f.provenance === 'sourced' && f.pestle.map((p) => <Badge key={p} plain>{p}</Badge>)}
            </div>
            <p className={`m-0 text-body ${f.provenance === 'not_found' ? 'text-secondary' : ''}`}>{f.finding}</p>
            {f.provenance === 'sourced' && (
              <p className="m-0 flex flex-wrap gap-x-3 gap-y-1">
                {f.sources.map((s) => (
                  <PageLink key={s.url} url={s.url} title={s.title} />
                ))}
              </p>
            )}
          </li>
        ))}
      </ol>
      <AsItCrossed text={text} />
    </div>
  );
}

function SupplierAnswer({ answer, text }: { answer: SupplierResearchAnswer; text: string }) {
  return (
    <div className="flex flex-col gap-4" data-testid="research-answer">
      <p className="m-0 text-dense text-secondary">
        {answer.marketCode} · {answer.products.map(productName).join(', ')} · searched {answer.researchedOn} · {answer.candidates.length} manufacturer
        {answer.candidates.length === 1 ? '' : 's'} read from their own sites
      </p>
      {answer.candidates.length === 0 && <p className="m-0 text-body">No maker was cited with a site that could be read.</p>}
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {answer.candidates.map((c) => (
          <li key={c.website} className="flex flex-col gap-1.5 border-l-2 border-line pl-3" data-testid="research-candidate">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-dense font-semibold">{c.name}</span>
              <PageLink url={c.website} />
            </div>
            {c.points.length > 0 ? (
              <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-1 @md:grid-cols-[minmax(10rem,auto)_1fr]">
                {c.points.map((p) => (
                  <div key={p.key} className="contents">
                    <dt className="text-dense text-meta">{POINT_LABEL.get(p.key) ?? p.key}</dt>
                    <dd className="m-0 text-dense">
                      {p.answer} <span className="text-secondary">“{p.quote}”</span> <PageLink url={p.url} />
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="m-0 text-dense text-secondary">Its pages named none of the things looked for.</p>
            )}
            {c.contact && (
              <p className="m-0 text-dense">
                <Kicker>Contact</Kicker> {[c.contact.name, c.contact.role].filter(Boolean).join(', ')}
                {c.contact.name || c.contact.role ? ' · ' : ''}
                {c.contact.address} <PageLink url={c.contact.url} />
              </p>
            )}
          </li>
        ))}
      </ol>
      {answer.skipped.length > 0 && (
        <details className="text-dense text-secondary">
          <summary className="cursor-pointer">{answer.skipped.length} left out, and why</summary>
          <ul className="m-0 mt-1 pl-5">
            {answer.skipped.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </details>
      )}
      <AsItCrossed text={text} />
    </div>
  );
}

function DistributorAnswer({ answer, text }: { answer: DistributorResearchAnswer; text: string }) {
  return (
    <div className="flex flex-col gap-4" data-testid="research-answer">
      <p className="m-0 text-dense text-secondary">
        {answer.marketCode} · {answer.channels.map((c) => DISTRIBUTOR_CHANNEL_WORDS[c]).join(', ')} · searched {answer.researchedOn} · {answer.candidates.length} distributor
        {answer.candidates.length === 1 ? '' : 's'} read from their own sites
      </p>
      {answer.candidates.length === 0 && <p className="m-0 text-body">No distributor was cited with a site that could be read.</p>}
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {answer.candidates.map((c) => (
          <li key={c.website} className="flex flex-col gap-1.5 border-l-2 border-line pl-3" data-testid="research-candidate">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-dense font-semibold">{c.name}</span>
              <PageLink url={c.website} />
            </div>
            {c.points.length > 0 ? (
              <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-1 @md:grid-cols-[minmax(10rem,auto)_1fr]">
                {c.points.map((p) => (
                  <div key={p.key} className="contents">
                    <dt className="text-dense text-meta">{DISTRIBUTOR_LABEL.get(p.key) ?? p.key}</dt>
                    <dd className="m-0 text-dense">
                      {p.answer} <span className="text-secondary">“{p.quote}”</span> <PageLink url={p.url} />
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="m-0 text-dense text-secondary">Its pages named none of the things looked for.</p>
            )}
          </li>
        ))}
      </ol>
      {answer.skipped.length > 0 && (
        <details className="text-dense text-secondary">
          <summary className="cursor-pointer">{answer.skipped.length} left out, and why</summary>
          <ul className="m-0 mt-1 pl-5">
            {answer.skipped.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </details>
      )}
      <AsItCrossed text={text} />
    </div>
  );
}
